import { useState, useEffect } from 'react';
import { Template } from '../../../mocks/websiteTemplates';
import { CATEGORY_META } from '../websiteBuilderConstants';

interface FavoritesDrawerProps {
  favorites: Template[];
  onClose: () => void;
  onRemove: (template: Template) => void;
  onPreview: (template: Template) => void;
  onClearAll: () => void;
}

export default function FavoritesDrawer({
  favorites,
  onClose,
  onRemove,
  onPreview,
  onClearAll,
}: FavoritesDrawerProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 280);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') handleClose();
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />

      {/* Drawer */}
      <div
        className={`fixed right-0 top-0 bottom-0 w-96 bg-white z-50 flex flex-col transition-transform duration-300 ease-out ${
          visible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-rose-50 border border-rose-100 flex items-center justify-center">
              <i className="ri-heart-3-fill text-rose-500 text-sm"></i>
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900">Saved Templates</h2>
              <p className="text-xs text-gray-500">{favorites.length} saved</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {favorites.length > 0 && (
              <button
                onClick={onClearAll}
                className="text-xs text-gray-400 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap px-2 py-1 rounded-lg hover:bg-red-50"
              >
                Clear all
              </button>
            )}
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {favorites.length === 0 ? (
            /* Empty state */
            <div className="flex flex-col items-center justify-center h-full px-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-rose-50 flex items-center justify-center mb-4">
                <i className="ri-heart-3-line text-rose-300 text-3xl"></i>
              </div>
              <h3 className="text-base font-semibold text-gray-800 mb-2">No saved templates yet</h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                Click the heart icon on any template to save it here for easy access later.
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {favorites.map((template) => {
                const meta = CATEGORY_META[template.category];
                return (
                  <div
                    key={template.name}
                    className="group flex gap-3 p-3 bg-white border border-gray-100 rounded-xl hover:border-gray-200 transition-all"
                  >
                    {/* Thumbnail */}
                    <div
                      className="flex-shrink-0 rounded-lg overflow-hidden cursor-pointer"
                      style={{ width: 80, height: 56 }}
                      onClick={() => { onPreview(template); handleClose(); }}
                    >
                      <img
                        src={template.image}
                        alt={template.name}
                        className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-200"
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{template.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5 mb-1.5">
                        <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-br ${meta?.gradient ?? 'from-violet-500 to-cyan-500'}`}></div>
                        <p className="text-xs text-gray-500 truncate">{template.category}</p>
                      </div>
                      <div className="flex gap-1">
                        {template.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-md">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => { onPreview(template); handleClose(); }}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-violet-100 text-gray-500 hover:text-violet-600 transition-colors cursor-pointer"
                        title="Preview"
                      >
                        <i className="ri-eye-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => onRemove(template)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                        title="Remove"
                      >
                        <i className="ri-heart-break-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer CTA */}
        {favorites.length > 0 && (
          <div className="px-4 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50">
            <button className="w-full py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-cyan-500 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap">
              <i className="ri-rocket-line"></i>
              Start Building with a Favorite
            </button>
            <p className="text-center text-xs text-gray-400 mt-2">
              {favorites.length} template{favorites.length !== 1 ? 's' : ''} saved this session
            </p>
          </div>
        )}
      </div>
    </>
  );
}
